import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { FacilityIdSchema } from "./facility.contract.js";
/**
 * Category read contract (ADR-007, P-B). Categories are flat, realm-wide labels grouping manuals inside a facility.
 * Reads are realm-visible (RLS-scoped) — no per-node permission gate. `GET /categories` feeds the attach picker;
 * `GET /facilities/:id/categories` returns a facility's attached categories (server-sorted by name). Mutations
 * (create/rename/delete, attach/detach) land in P-C. Browser-safe: zod + ts-rest only.
 */
const c = initContract();
/** One category as sent to the client — realm is implicit (the caller's tenant). Icon deferred to the assets plan. */
export const CategorySchema = z.object({
    id: z.number().int(),
    name: z.string(),
    description: z.string().nullable(),
    /**
     * How many facilities in the realm have this category attached (active joins). Populated by the LIST read
     * (`GET /categories`) to drive the picker's per-category count; **optional** because create/update mutation
     * responses that return a Category don't compute it. Mirrors `FacilityNodeSchema.categoryCount`.
     */
    facilityCount: z.number().int().optional(),
    /**
     * The category ICON (ADR-043) — the image the manual CARD renders for every manual in this category, falling back
     * to the realm's `standard_category_icon_id` when null (`COALESCE(c.image_id, r.standard_category_icon_id)`).
     */
    imageFileId: z.number().int().nullable(),
    /**
     * A presigned GET for {@link imageFileId}, or null when there is no icon / storage is unconfigured. Signed for the
     * **raw** object, never a thumbnail: category icons are transparent PNGs and the legacy thumbnailer flattened them
     * onto a blank-grey JPEG. **Short-lived** — treat it as display-only and never persist it.
     */
    imageUrl: z.string().nullable(),
    /**
     * The row's `updated_at`, ISO-8601 with milliseconds — the optimistic-concurrency token. Pass it back as
     * `updateCategory.expectedUpdatedAt`; a category someone else changed meanwhile is a 409, never a silent
     * overwrite. Same mechanism as `RoleCard.updatedAt` / `patchRole`.
     */
    updatedAt: z.string(),
});
export const CategoryListSchema = z.object({ categories: z.array(CategorySchema) });
const UnauthorizedSchema = z.object({ message: z.string() });
/** A category id path param, bounded to Postgres int4 (see FacilityIdSchema for the rationale). */
export const CategoryIdSchema = z.coerce.number().int().positive().max(2147483647);
const Forbidden = z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") });
const CategoryNotFound = z.object({ message: z.string(), code: z.literal("CATEGORY_NOT_FOUND") });
export const categoryContract = c.router({
    listCategories: {
        method: "GET",
        path: "/categories",
        responses: {
            200: CategoryListSchema,
            401: UnauthorizedSchema,
        },
        summary: "All categories in the caller's realm, sorted by name (the attach picker).",
    },
    getFacilityCategories: {
        method: "GET",
        path: "/facilities/:id/categories",
        pathParams: z.object({ id: FacilityIdSchema }),
        responses: {
            200: CategoryListSchema,
            401: UnauthorizedSchema,
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "A facility's attached categories, sorted by name (opaque 404 if the facility is absent in-tenant).",
    },
    // ── Mutations (ADR-007 §2/§3, P-C). Authz `manage`; bypass tier = super_admin OR HPFP=access_manage. Category CRUD is
    // realm-wide (a caller who manages ≥1 facility, or the bypass tier); attach/detach additionally need manage on the
    // target facility. Ownership-checked under RLS. Explicit routes — no implicit CRUD.
    createCategory: {
        method: "POST",
        path: "/categories",
        body: z.object({ name: z.string().trim().min(1).max(255), description: z.string().nullable().optional() }),
        responses: {
            200: CategorySchema,
            401: UnauthorizedSchema,
            403: Forbidden,
            409: z.object({ message: z.string(), code: z.literal("CATEGORY_NAME_TAKEN") }),
        },
        summary: "Create a realm-wide category (authz: manage; name unique per realm).",
    },
    updateCategory: {
        method: "PATCH",
        path: "/categories/:id",
        pathParams: z.object({ id: CategoryIdSchema }),
        body: z.object({
            name: z.string().trim().min(1).max(255).optional(),
            description: z.string().nullable().optional(),
            /**
             * The category ICON (ADR-043). Three states, distinguished as `undefined` / number / `null`:
             * **absent** leaves it alone · a **file id** sets it · **`null`** clears it.
             *
             * `.positive()` deliberately rejects the legacy `0` sentinel (the legacy sent `imageId: 0` to clear and an
             * interceptor mapped it to NULL). Our column is honestly nullable, so `0` is a client bug and earns a 400
             * rather than silently deleting the icon.
             *
             * The referenced file must be live, `status = 'ready'`, an image by `IMAGE_UPLOAD_EXTENSIONS`, and inside the
             * realm's **Master** asset subtree — the scope a picker can actually browse, and the legacy's own file-selection
             * scope. Anything else is an opaque 404 (see responses).
             */
            imageId: z.number().int().positive().nullable().optional(),
            /**
             * The `updatedAt` of the Category this edit was composed against (REQUIRED, like `patchRole`'s).
             *
             * Without it the UPDATE is unconditional, so two people editing one category let the OLDER write land last —
             * and a category's icon is the card image for every manual in every facility it is attached to, so a stale
             * write is visible far beyond the category itself. An OPTIONAL token would be one people forget exactly when
             * it matters, which is why this mirrors `patchRole` and requires it.
             */
            expectedUpdatedAt: z.string().min(1),
        }),
        responses: {
            200: CategorySchema,
            401: UnauthorizedSchema,
            403: Forbidden,
            /**
             * The category, OR the referenced `imageId` file. Deliberately the SAME code for both, and for every reason a
             * file can be rejected — absent, other tenant, soft-deleted, not `ready`, outside the Master subtree — so the
             * response never becomes an existence oracle for files the caller may not browse (ADR-043 D3).
             */
            404: CategoryNotFound.or(z.object({ message: z.string(), code: z.literal("FILE_NOT_FOUND") })),
            /** `CATEGORY_NAME_TAKEN`, or `CATEGORY_STALE` when `expectedUpdatedAt` no longer matches — someone else edited
             *  this category since it was loaded, so the write is refused rather than silently overwriting theirs. */
            409: z
                .object({ message: z.string(), code: z.literal("CATEGORY_NAME_TAKEN") })
                .or(z.object({ message: z.string(), code: z.literal("CATEGORY_STALE") })),
            /** The file exists and is reachable, but is not an image — safe to distinguish, since its extension is already
             *  visible to a caller who could browse it. */
            422: z.object({ message: z.string(), code: z.literal("FILE_NOT_AN_IMAGE") }),
        },
        summary: "Rename / re-describe / re-icon a category (ownership-checked under RLS).",
    },
    deleteCategory: {
        method: "DELETE",
        path: "/categories/:id",
        pathParams: z.object({ id: CategoryIdSchema }),
        responses: {
            200: z.object({ deleted: z.boolean() }),
            401: UnauthorizedSchema,
            403: Forbidden,
            404: CategoryNotFound,
            // 409 carries the attached facilities (id + name) so the client can show WHERE to detach it — the error
            // dialog lists them (expandable). Sorted by name.
            409: z.object({
                message: z.string(),
                code: z.literal("CATEGORY_HAS_ATTACHMENTS"),
                facilities: z.array(z.object({ id: z.number().int(), name: z.string() })),
            }),
        },
        summary: "Soft-delete a category — 409 (with the attached facilities) while any facility still has it attached.",
    },
    attachCategory: {
        method: "POST",
        path: "/facilities/:id/categories",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({ categoryId: CategoryIdSchema }),
        responses: {
            200: z.object({ attached: z.boolean() }),
            401: UnauthorizedSchema,
            403: Forbidden,
            404: z.object({ message: z.string(), code: z.union([z.literal("FACILITY_NOT_FOUND"), z.literal("CATEGORY_NOT_FOUND")]) }),
        },
        summary: "Attach a category to a facility (authz: manage on the facility; idempotent).",
    },
    detachCategory: {
        method: "DELETE",
        path: "/facilities/:id/categories/:categoryId",
        pathParams: z.object({ id: FacilityIdSchema, categoryId: CategoryIdSchema }),
        responses: {
            200: z.object({ detached: z.boolean() }),
            401: UnauthorizedSchema,
            403: Forbidden,
            404: z.object({ message: z.string(), code: z.union([z.literal("FACILITY_NOT_FOUND"), z.literal("FACILITY_CATEGORY_NOT_FOUND")]) }),
            409: z.object({ message: z.string(), code: z.literal("FACILITY_CATEGORY_HAS_MANUALS") }),
        },
        summary: "Detach a category from a facility (authz: manage on the facility; 409 once it has manuals).",
    },
});
//# sourceMappingURL=category.contract.js.map